// TROIA-V3-FRONTEND/src/modules/crm/ranking/pages/RankingPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import type { GamificationPeriod, GamificationRankingScope, RankingQuery } from '@troia-v3/types';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import {
  Button, DashCard, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  ListPageEmpty, ListPageLoading, PageToolbar, PeriodRangeButton, Podium, RankingList, Tooltip,
} from '@/shared/components/ui';
import { detectPeriod, formatPeriodLabel, formatRangeLabel, previousPeriodKeys } from '@/shared/utils/gamificationPeriod';

import { OnlyMeCard } from '../components/OnlyMeCard';
import { RankingScopeSelector, parseScopeItem, scopeItemId, scopeNeedsId, useScopeItems, type ScopeSelection } from '../components/RankingScopeSelector';
import { useGamificationMe, useRanking } from '../hooks/useRanking';

const PERIODS: Array<{ id: GamificationPeriod; label: string }> = [{ id: 'week', label: 'Semana' }, { id: 'month', label: 'Mês' }, { id: 'quarter', label: 'Trimestre' }];
const PREVIOUS_LABEL: Record<GamificationPeriod, string> = { week: 'Semanas anteriores', month: 'Meses anteriores', quarter: 'Trimestres anteriores' };
/** Mesmo teto do backend (`RANKING_RANGE_MAX_DAYS`). */
const RANGE_MAX_DAYS = 366;
/**
 * Chave POR USUÁRIO (item G da onda final): o recorte é uma preferência
 * pessoal, e num computador compartilhado (balcão de loja) a chave global fazia
 * o próximo vendedor herdar o recorte do anterior — inclusive um
 * `users_by_team:{id}` de uma equipe que ele nem enxerga.
 */
const storageKey = (userId: string): string => `gamification_ranking_scope:${userId}`;

/** Recorte SEM id → seleção com label amigável. Nunca chamada com `users_by_team`/`users_by_unit` (esses só existem colados num id vindo do seletor). */
function selectionFromScope(scope: GamificationRankingScope): ScopeSelection {
  if (scope === 'teams') return { scope: 'teams', label: 'Ranking de equipes' };
  if (scope === 'units') return { scope: 'units', label: 'Ranking de unidades' };
  return { scope: 'users', label: 'Vendedores da empresa' };
}

/** Mensagem real do backend (`{ success: false, message }`) — nunca o texto genérico do axios ("Request failed with status code 422"). */
function extractErrorMessage(error: unknown, fallback: string): string {
  const maybeAxios = error as { response?: { data?: { message?: string } } } | undefined;
  return maybeAxios?.response?.data?.message || fallback;
}

/**
 * /crm/ranking — mockup lote-b/09. Seletor de recorte no header (estilo `.funnel`),
 * período configurado pelo admin (segmented travado), pódio + lista; 4 variantes
 * conforme `visibility` e recorte. Sem `gamification:read` aqui — todo vendedor vê.
 */
export const RankingPage: React.FC = () => {
  const { state } = useAuth();
  const userId = state.user?.id ?? '';

  // Sem recorte salvo (ou descartado após 422) → `null`: a 1ª query sai SEM
  // `scope` e o backend escolhe `config.rankings[0]`; o estado passa a
  // refletir o recorte EFETIVO devolvido em `data.scope` (Fix round 1, item 1
  // — antes o bootstrap fixava `scope: 'users'` e, se o admin tivesse
  // desligado esse recorte, a página nunca se recuperava do 422).
  const [selection, setSelection] = useState<ScopeSelection | null>(() => {
    const saved = userId ? localStorage.getItem(storageKey(userId)) : null;
    return saved ? parseScopeItem(saved, '') : null;
  });
  // Recorte salvo virou 422 (admin desligou o recorte depois de salvo, ou o
  // localStorage tinha lixo) → descarta e refaz SEM `scope`. 1 retry só —
  // nunca loop, mesmo que o fallback também falhe (ex.: `rankings[0]` exige
  // teamId/unitId que não temos).
  const [scopeRetried, setScopeRetried] = useState(false);
  const [periodKey, setPeriodKey] = useState<string | undefined>(undefined);
  // Intervalo personalizado (2026-10-16): soma pela data do evento, fora do
  // ciclo configurado. Exclui `periodKey` (o backend recusa os dois juntos).
  const [customRange, setCustomRange] = useState<{ start: string; end: string } | null>(null);

  const query = useMemo<RankingQuery>(() => ({
    ...(selection ? { scope: selection.scope } : {}),
    ...(selection?.teamId ? { teamId: selection.teamId } : {}),
    ...(selection?.unitId ? { unitId: selection.unitId } : {}),
    ...(customRange
      ? { startDate: customRange.start, endDate: customRange.end }
      : periodKey ? { periodKey } : {}),
  }), [selection, periodKey, customRange]);

  const { data, isLoading, error, dataUpdatedAt } = useRanking(query);
  const visibility = data?.visibility ?? 'full';
  const { data: me } = useGamificationMe(visibility === 'only_me');
  const { items: scopeItems, isLoading: scopeItemsLoading } = useScopeItems(data?.rankings ?? []);

  // Com intervalo, `data.periodKey` é o rótulo do intervalo — o seletor de
  // períodos anteriores continua ancorado na última chave de ciclo recebida.
  const [lastCycleKey, setLastCycleKey] = useState('');
  useEffect(() => { if (data && !data.range) setLastCycleKey(data.periodKey); }, [data]);

  const applyCustomRange = (range: { start: string; end: string }): void => {
    const days = (Date.parse(range.end) - Date.parse(range.start)) / 86_400_000 + 1;
    if (days > RANGE_MAX_DAYS) {
      toast.error(`Escolha um intervalo de até ${RANGE_MAX_DAYS} dias`);
      return;
    }
    setCustomRange(range);
    setPeriodKey(undefined);
  };

  useEffect(() => {
    // 422 de intervalo não é recorte inválido — não descarta o recorte salvo.
    if (!error || scopeRetried || customRange) return;
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 422) return;
    if (userId) localStorage.removeItem(storageKey(userId));
    setScopeRetried(true);
    setSelection(null);
  }, [error, scopeRetried, userId, customRange]);

  // Recorte restaurado do localStorage vem só com o id (`users_by_team:{id}`) —
  // o nome da equipe/unidade mora no dropdown. Assim que a lista carrega,
  // adota o rótulo do item casado; se o id não existe mais (equipe apagada, ou
  // o admin desligou aquele recorte), trata como "não havia nada salvo" e
  // volta ao bootstrap (item G da onda final). Sem isso o header mostrava
  // "Vendedores" pelado e o seletor aparecia sem seleção.
  useEffect(() => {
    // `data` é pré-requisito: os dropdowns de equipe/unidade só ligam com
    // `data.rankings` em mãos. Antes de a config chegar a lista está vazia
    // porque as queries estão DESLIGADAS — não porque o id sumiu.
    if (!data || !selection || !scopeNeedsId(selection.scope) || selection.label !== '') return;
    if (!data.rankings.includes(selection.scope)) return; // recorte desligado — o efeito acima resolve
    if (scopeItemsLoading) return;
    const match = scopeItems.find((i) => i.id === scopeItemId(selection));
    if (match) { setSelection(parseScopeItem(match.id, match.name)); return; }
    if (userId) localStorage.removeItem(storageKey(userId));
    setSelection(null);
  }, [data, scopeItems, scopeItemsLoading, selection, userId]);

  // 1ª resposta sem recorte na query (bootstrap ou pós-retry) → adota o
  // recorte efetivo que o backend escolheu (`data.scope`, sempre presente).
  // Exceção: se a config só tem recortes COM id, o backend devolve 200 vazio
  // com `scope: 'users_by_unit'` (ou `_team`) — adotar isso viraria uma
  // seleção sem id e a próxima query voltaria 422. Fica sem seleção mesmo: o
  // seletor monta com as equipes/unidades e o clique do usuário traz o id.
  useEffect(() => {
    if (data && !selection && !scopeNeedsId(data.scope)) setSelection(selectionFromScope(data.scope));
  }, [data, selection]);

  // recorte salvo que o admin desligou → volta para o primeiro disponível SEM
  // id; se todos exigirem id, volta ao bootstrap (seleção nula) em vez de
  // insistir num recorte impossível de consultar.
  useEffect(() => {
    if (!data || !selection || data.rankings.includes(selection.scope)) return;
    const fallback = data.rankings.find((s) => !scopeNeedsId(s));
    setSelection(fallback ? selectionFromScope(fallback) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selection?.scope]);
  useEffect(() => { if (selection && userId) localStorage.setItem(storageKey(userId), scopeItemId(selection)); }, [selection, userId]);

  // Recorte "ativo" pra renderização: nunca espera o efeito de adoção rodar —
  // no 1º render em que `data` já chegou mas `selection` ainda é `null`,
  // deriva direto de `data.scope` (o efeito só persiste o estado depois).
  // Recorte que exige id entra sem label: o seletor cai no placeholder
  // ("Selecionar recorte") em vez de mentir um nome que ninguém escolheu.
  const activeSelection: ScopeSelection = selection ?? (
    data
      ? (scopeNeedsId(data.scope) ? { scope: data.scope, label: '' } : selectionFromScope(data.scope))
      : { scope: 'users', label: 'Vendedores da empresa' }
  );

  // Config só com recortes por equipe/unidade: o backend devolve 200 vazio até
  // o usuário escolher no seletor. Mostrar a lista vazia ("Ninguém pontuou…")
  // seria mentira — o certo é pedir a escolha.
  const needsScopePick = scopeNeedsId(activeSelection.scope) && !activeSelection.teamId && !activeSelection.unitId;

  const period: GamificationPeriod = data?.period ?? detectPeriod(lastCycleKey || '2026-01');
  const currentKey = data && !data.range ? data.periodKey : lastCycleKey;
  const baseKey = periodKey ?? currentKey;
  const periodLabel = customRange ? formatRangeLabel(customRange.start, customRange.end) : formatPeriodLabel(period, baseKey);
  const updatedMin = Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 60000));
  const isEntity = activeSelection.scope === 'teams' || activeSelection.scope === 'units';
  const scopeSuffix = activeSelection.label.replace(/^Vendedores · /, '');
  const title = activeSelection.scope === 'units' ? 'Ranking de unidades' : activeSelection.scope === 'teams' ? 'Ranking de equipes' : activeSelection.scope === 'users' ? 'Vendedores da empresa' : scopeSuffix ? `Vendedores — ${scopeSuffix}` : 'Vendedores';

  // Gap "Faltam N pts para o #3" só acompanha a linha separada de "você" (mockup 09-B) —
  // em `full` o backend também calcula `gapToNext` (gap ao vizinho de cima), mas o mockup
  // NÃO mostra esse texto na variante A (o usuário já aparece destacado na lista inteira).
  const showGap = visibility === 'top3_and_me' && data?.me !== undefined && data.me.position > 3 && data.me.gapToNext !== undefined && data.me.gapToNext > 0;

  const errorMessage = error ? extractErrorMessage(error, 'Não foi possível carregar o ranking agora. Tente novamente em instantes.') : undefined;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="ranking-page">
      <PageToolbar
        data-testid="ranking-toolbar"
        left={<>
          <RankingScopeSelector items={scopeItems} selectedId={scopeItemId(activeSelection)} onSelect={(s) => { setSelection(s); setPeriodKey(undefined); }} />
          {currentKey && <span className="inline-flex items-center h-[26px] px-[11px] rounded-full text-xs font-semibold tabular-nums flex-none" style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }} data-testid="ranking-period-chip">{periodLabel}</span>}
        </>}
        right={<>
          {/* Segmentado "sunken" — réplica do segmentado do `PeriodPicker` (DS): trilho `--theme-card-border`, ativo com fundo de card.
              Só o período configurado é clicável — com intervalo personalizado ativo, ele volta para o ciclo atual. */}
          <div className="hidden md:inline-flex items-center rounded-[9px] p-[3px] gap-0.5" style={{ backgroundColor: 'var(--theme-card-border)' }} role="radiogroup" aria-label="Período de apuração">
            {PERIODS.map((p) => {
              const configured = p.id === period;
              const active = configured && !customRange;
              const btn = (
                <button key={p.id} type="button" role="radio" aria-checked={active} disabled={!configured} onClick={() => setCustomRange(null)} className="h-[30px] px-[13px] rounded-[7px] text-[13px] font-semibold disabled:cursor-default" style={active ? { backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)', boxShadow: '0 1px 2px rgba(0,0,0,.06)' } : { color: 'var(--theme-muted-text)' }} data-testid={`ranking-period-${p.id}`}>{p.label}</button>
              );
              return configured ? btn : <Tooltip key={p.id} content="Definido em Configurações › Gamificação">{btn}</Tooltip>;
            })}
          </div>
          {visibility !== 'only_me' && (
            <PeriodRangeButton
              active={customRange !== null}
              {...(customRange ? { start: customRange.start, end: customRange.end } : {})}
              onApply={applyCustomRange}
              collapsibleLabel
              data-testid="ranking-period"
            />
          )}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" icon="clock" collapsibleLabel data-testid="ranking-previous">{PREVIOUS_LABEL[period]}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => { setPeriodKey(undefined); setCustomRange(null); }} data-testid="ranking-previous-current">Atual · {currentKey ? formatPeriodLabel(period, currentKey) : ''}</DropdownMenuItem>
              {currentKey && previousPeriodKeys(currentKey, 6).map((k) => (
                <DropdownMenuItem key={k} onSelect={() => { setPeriodKey(k); setCustomRange(null); }} data-testid={`ranking-previous-${k}`}>{formatPeriodLabel(period, k)}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>}
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-4">
          {isLoading && !data && <ListPageLoading message="Carregando ranking..." />}
          {error && <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="ranking-error">{errorMessage}</div>}

          {data && data.rankings.length === 0 && (
            <ListPageEmpty icon="award" message="Gamificação desligada" description="Quando a empresa ligar a pontuação em Configurações › Gamificação, o ranking aparece aqui." />
          )}

          {data && visibility === 'only_me' && me && <OnlyMeCard me={me} period={period} />}

          {data && visibility !== 'only_me' && data.rankings.length > 0 && needsScopePick && (
            <ListPageEmpty icon="users" message="Escolha uma equipe ou unidade" description="A empresa ranqueia os vendedores por equipe/unidade. Selecione o recorte no seletor acima para ver a lista." />
          )}

          {data && visibility !== 'only_me' && !needsScopePick && (
            <>
              {data.entries.length > 0 && (
                <div className="rounded-xl" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                  <Podium entries={data.entries} scope={activeSelection.scope} meId={userId} />
                </div>
              )}
              <DashCard
                icon={activeSelection.scope === 'units' ? 'map-pin' : 'users'}
                title={title}
                subtitle={
                  isEntity
                    ? (activeSelection.scope === 'units' ? 'Soma dos pontos confirmados dos vendedores de cada loja' : 'Soma dos pontos confirmados dos vendedores de cada equipe')
                    : visibility === 'top3_and_me' ? 'A empresa mostra o pódio e a sua posição'
                      : `${data.total} ${data.total === 1 ? 'vendedor' : 'vendedores'} · atualizado há ${updatedMin} min`
                }
                headerRight={visibility === 'full' && !isEntity ? (
                  <div className="flex items-center gap-2">
                    <span className="h-[26px] px-[11px] rounded-full text-xs font-semibold inline-flex items-center" style={{ background: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>Pontos</span>
                    <span className="h-[26px] px-[11px] rounded-full text-xs font-semibold inline-flex items-center gap-1" style={{ color: 'var(--theme-muted-text)', border: '1px solid var(--theme-input-border)' }}>% da meta <span className="text-[10px] opacity-70">em breve</span></span>
                  </div>
                ) : undefined}
                testId="ranking-card"
                noPadding
              >
                <RankingList
                  entries={data.entries}
                  scope={activeSelection.scope}
                  visibility={visibility}
                  total={data.total}
                  meId={userId}
                  {...(data.me ? { me: data.me } : {})}
                  {...(showGap
                    ? { gapText: <>Faltam <b style={{ color: 'var(--theme-card-text)' }}>{data.me?.gapToNext} pts</b> para o #3 — uma venda fechada resolve.</> }
                    : {})}
                />
              </DashCard>
            </>
          )}

          <div className="text-xs text-center" style={{ color: 'var(--theme-muted-text)' }} data-testid="ranking-footnote">
            Pontos pendentes (aguardando confirmação no WhatsApp) não entram no ranking · estornos de vendas canceladas descontam automaticamente
          </div>
        </div>
      </div>
    </div>
  );
};
RankingPage.displayName = 'RankingPage';
